import {GithubGist} from "./typesGists";
import * as React from "react";

export enum LoginType {
  google = "google",
  github = "github",
  email = "email",
}

export enum SubscriptionTier {
  free = "free",
  plus = "plus",
  premium = "premium"
}

export const SubscriptionTierDisplayName: Record<SubscriptionTier, string> = {
  [SubscriptionTier.free]: "Free",
  [SubscriptionTier.plus]: "Plus",
  [SubscriptionTier.premium]: "Premium",
};

export const subscriptionTierToDisplayName = (tier: SubscriptionTier): string => {
  if (Object.values(SubscriptionTier).includes(tier as SubscriptionTier)) {
    return SubscriptionTierDisplayName[tier];
  }
  return 'Other'
};

export const subscriptionTierFromDisplayName = (displayName: string): SubscriptionTier => {
  const normalizedDisplayName = displayName.trim().toLowerCase();

  if (normalizedDisplayName === SubscriptionTier.free) {
    return SubscriptionTier.free;
  }

  if (normalizedDisplayName === SubscriptionTier.plus) {
    return SubscriptionTier.plus;
  }

  if (normalizedDisplayName === SubscriptionTier.premium) {
    return SubscriptionTier.premium;
  }

  console.error(`Unknown subscription tier: ${displayName}`);
  return SubscriptionTier.free;
};

export const subscriptionTierToBacktestYearsLookbackLimit = (tier: SubscriptionTier): number => {
  if (tier === SubscriptionTier.free) {
    return 2;
  } else if (tier === SubscriptionTier.plus) {
    return 6;
  } else if (tier === SubscriptionTier.premium) {
    return 12;
  }
  console.error("Unexpected subscription tier: ", tier)
  return 2;
}

export interface AuthenticationState {
  status?: string;
  access_token?: string;
  expires_in?: number;
  refresh_token?: string;
  refresh_token_expires_in?: number;
  scope?: string | null;
  token_type?: string;
  login_type: LoginType;
  junkyard_access_token?: string;
  junkyard_expires_in?: number;
}

export interface AuthContextType {
  readAccessToken: () => string | null;
  storeAccessToken: (access_token: string | null) => void;
  setCurrentUser: (user: string | null) => void;
  getCurrentUser: () => string | null;
  isAuthenticated: () => boolean;
  getLoginType: () => LoginType | undefined;
  storeLoginType: (loginType: LoginType) => void;
  logout: () => void;
  getSubscriptionTier: () => SubscriptionTier;
}

export interface LoggedInUserDetail {
  name?: string;
}

export interface MarketingInterestRequest {
  email: string;
}

export interface MarketingInterestResponse {
  status: string;
  message: string;
}

export interface GithubSignInPrepResponse {
  status: string;
  full_url: string;
}

export interface GithubSignInCallbackRequest {
  code: string;
  state: string
}

export interface GithubLoggedInRequest{
  access_token: string;
  page: number;
  per_page: number;
}

export interface GoogleLoggedInRequest{
  access_token: string;
  page: number;
  per_page: number;
}

export interface GithubLoggedInResponse {
  name: string;
}

export interface GoogleAccountInfoResponse {
  first_name: string;
  last_name: string;
  full_name: string;
}

export interface AccountDetails {
  email_address: string;
  first_name?: string;
  last_name?: string;
  subscription_tier: SubscriptionTier;
}

export interface GithubGistDetailRequest {
  access_token: string;
  gist_id: string;
}

export interface GithubGistUpdateRequest {
  access_token: string;
  updated_gist: GithubGist;
}

export interface GithubGistUpdateResponse {
  access_token: string;
  updated_gist: GithubGist;
}

export interface GoogleSignInPrepResponse {
  status: string;
  full_url: string;
}

export interface GoogleSignInCallbackRequest {
  code: string;
  state: string
}

export interface EmailManualSignInRequest {
  email: string;
  password: string;
}

export interface EmailManualSignUpResponse {
  message: string;
  account_details?: AccountDetails;
  email_verification_sent: boolean;
  access_token?: string;
  expires_in?: number;
}

export interface EmailManualSignInResponse {
  account_details?: AccountDetails;
  message: string;
  access_token?: string;
  expires_in?: number;
}

export interface EmailVerificationRequest {
  email: string;
  verification_code: string;
}

export interface EmailVerificationResponse {
  success: boolean;
  access_token: string;
  expires_in: number;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetVerifiedRequest extends PasswordResetRequest {
  verification_code: string;
  new_password: string;
}

export interface PasswordResetResponse {
  success: boolean;
}

export type SidebarTab = 'strategies' | 'ai-assistant';
